import { getWorkspaceId } from '../../lib/auth-storage';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { integrationApi, type GatewayDashboard } from '../../api/integration';

const NAV = [
  { to: '/integrations', label: 'Integration Hub' },
  { to: '/integrations/marketplace', label: 'Connector Marketplace' },
  { to: '/integrations/gateway', label: 'API Gateway' },
  { to: '/integrations/webhooks', label: 'Webhook Manager' },
  { to: '/integrations/developer', label: 'Developer Portal' },
  { to: '/integrations/transformations', label: 'Transformation Designer' },
  { to: '/integrations/health', label: 'Connector Health' },
  { to: '/integrations/analytics', label: 'API Analytics' },
  { to: '/integrations/logs', label: 'Connector Logs' },
];

export function APIGatewayPage() {
  const [dashboard, setDashboard] = useState<GatewayDashboard | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void integrationApi.gatewayDashboard((getWorkspaceId() ?? '')).then(setDashboard).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">API Gateway Dashboard</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        Routing, authentication, rate limiting and platform connectivity for all external integrations.
      </p>
      <nav className="mt-4 flex flex-wrap gap-3 text-sm">
        {NAV.map((l) => (
          <Link key={l.to} to={l.to} className="text-teal-700 hover:underline dark:text-teal-400">{l.label}</Link>
        ))}
      </nav>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'API Keys', value: dashboard?.api_keys ?? 0 },
          { label: 'Webhooks', value: dashboard?.webhooks ?? 0 },
          { label: 'Connectors', value: dashboard?.connectors ?? 0 },
          { label: 'Routes', value: dashboard?.routes?.length ?? 0 },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <p className="text-sm text-slate-500">{s.label}</p>
            <p className="mt-1 text-2xl font-semibold">{s.value}</p>
          </div>
        ))}
      </div>
      {dashboard?.routes && (
        <div className="mt-8 overflow-x-auto rounded-xl border dark:border-slate-700">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th className="px-4 py-2 text-left">Path</th>
                <th className="px-4 py-2 text-left">Method</th>
                <th className="px-4 py-2 text-left">Auth</th>
                <th className="px-4 py-2 text-left">Rate Limit</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.routes.map((r) => (
                <tr key={r.id} className="border-t dark:border-slate-700">
                  <td className="px-4 py-2 font-mono text-xs">{r.path}</td>
                  <td className="px-4 py-2">{r.method}</td>
                  <td className="px-4 py-2">{r.auth_type}</td>
                  <td className="px-4 py-2">{r.rate_limit_rps} rps</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dashboard?.platform_health && dashboard.platform_health.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium">Platform Health</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {dashboard.platform_health.map((m) => (
              <div key={m.module} className="rounded-lg border px-3 py-2 text-sm dark:border-slate-700">
                <span className="font-medium capitalize">{m.module}</span>
                <span className={`ml-2 ${m.status === 'healthy' ? 'text-green-600' : 'text-amber-600'}`}>{m.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
